import React, { useState, useEffect } from 'react';
import { replaceLocationVariables, fetchLocationByName } from '@/utils/variableReplacer';

interface PostPreviewProps {
  title: string;
  excerpt: string;
  content: string;
  locationName: string | null;
  // Add these new props for proper variable replacement
  author?: string;
  categories?: string[];
  tags?: string[];
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string[];
}

const PostPreview: React.FC<PostPreviewProps> = ({ 
  title, 
  excerpt, 
  content, 
  locationName,
  author,
  categories,
  tags,
  seoTitle,
  seoDescription,
  seoKeywords
}) => {
  const [previewTitle, setPreviewTitle] = useState(title);
  const [previewExcerpt, setPreviewExcerpt] = useState(excerpt);
  const [previewAuthor, setPreviewAuthor] = useState(author || '');
  const [previewCategories, setPreviewCategories] = useState(categories || []);
  const [previewTags, setPreviewTags] = useState(tags || []);
  const [previewSeoTitle, setPreviewSeoTitle] = useState(seoTitle || '');
  const [previewSeoDescription, setPreviewSeoDescription] = useState(seoDescription || '');
  const [previewSeoKeywords, setPreviewSeoKeywords] = useState(seoKeywords || []);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    console.log('PostPreview useEffect triggered:', { title, locationName });
    
    if (!locationName) {
      // Keep original template variables as-is for template posts
      setPreviewTitle(title);
      setPreviewExcerpt(excerpt);
      setPreviewAuthor(author || '');
      setPreviewCategories(categories || []);
      setPreviewTags(tags || []);
      setPreviewSeoTitle(seoTitle || '');
      setPreviewSeoDescription(seoDescription || '');
      setPreviewSeoKeywords(seoKeywords || []);
      return;
    }

    const loadLocationPreview = async () => {
      setIsLoading(true);
      try {
        console.log('Fetching location data for:', locationName);
        const location = await fetchLocationByName(locationName);
        if (location) {
          console.log('Location found, replacing variables:', location);
          const replacedTitle = replaceLocationVariables(title, location);
          const replacedExcerpt = replaceLocationVariables(excerpt, location);
          const replacedAuthor = replaceLocationVariables(author || '', location);
          const replacedCategories = categories?.map(cat => replaceLocationVariables(cat, location)) || [];
          const replacedTags = tags?.map(tag => replaceLocationVariables(tag, location)) || [];
          const replacedSeoTitle = replaceLocationVariables(seoTitle || '', location);
          const replacedSeoDescription = replaceLocationVariables(seoDescription || '', location);
          const replacedSeoKeywords = seoKeywords?.map(keyword => replaceLocationVariables(keyword, location)) || [];
          
          setPreviewTitle(replacedTitle);
          setPreviewExcerpt(replacedExcerpt);
          setPreviewAuthor(replacedAuthor);
          setPreviewCategories(replacedCategories);
          setPreviewTags(replacedTags);
          setPreviewSeoTitle(replacedSeoTitle);
          setPreviewSeoDescription(replacedSeoDescription);
          setPreviewSeoKeywords(replacedSeoKeywords);
        } else {
          console.log('No location found, keeping original template variables');
          // Keep original template variables if location not found
          setPreviewTitle(title);
          setPreviewExcerpt(excerpt);
          setPreviewAuthor(author || '');
          setPreviewCategories(categories || []);
          setPreviewTags(tags || []);
          setPreviewSeoTitle(seoTitle || '');
          setPreviewSeoDescription(seoDescription || '');
          setPreviewSeoKeywords(seoKeywords || []);
        }
      } catch (error) {
        console.error('Error loading location preview:', error);
        // Keep original template variables on error
        setPreviewTitle(title);
        setPreviewExcerpt(excerpt);
        setPreviewAuthor(author || '');
        setPreviewCategories(categories || []);
        setPreviewTags(tags || []);
        setPreviewSeoTitle(seoTitle || '');
        setPreviewSeoDescription(seoDescription || '');
        setPreviewSeoKeywords(seoKeywords || []);
      } finally {
        setIsLoading(false);
      }
    };

    loadLocationPreview();
  }, [title, excerpt, locationName, author, categories, tags, seoTitle, seoDescription, seoKeywords]);

  if (isLoading) {
    return (
      <div>
        <div className="h-6 bg-gray-200 rounded animate-pulse mb-2"></div>
        <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-xl mt-2 line-clamp-2 font-semibold">{previewTitle}</h3>
      {previewExcerpt && (
        <p className="text-sm text-muted-foreground line-clamp-3 mb-3">{previewExcerpt}</p>
      )}
      
      {/* Show preview of other fields if they exist */}
      {previewAuthor && (
        <p className="text-xs text-muted-foreground mb-2">By {previewAuthor}</p>
      )}
      
      {(previewCategories.length > 0 || previewTags.length > 0) && (
        <div className="flex flex-wrap gap-1 mb-2">
          {previewCategories.map((category, index) => (
            <span key={index} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
              {category}
            </span>
          ))}
          {previewTags.map((tag, index) => (
            <span key={index} className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">
              #{tag}
            </span>
          ))}
        </div>
      )}
      
      {(previewSeoTitle || previewSeoDescription || previewSeoKeywords.length > 0) && (
        <div className="text-xs text-muted-foreground mt-2 p-2 bg-blue-50 rounded">
          <div className="font-medium mb-1">SEO Preview:</div>
          {previewSeoTitle && <div>Title: {previewSeoTitle}</div>}
          {previewSeoDescription && <div>Description: {previewSeoDescription}</div>}
          {previewSeoKeywords.length > 0 && (
            <div>Keywords: {previewSeoKeywords.join(', ')}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default PostPreview;
